"use client"
import React, { useState, useCallback } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"

interface CollapsibleProps {
  children: React.ReactNode
  defaultOpen?: boolean
}

export const Collapsible: React.FC<CollapsibleProps> = ({ children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  return <CollapsibleContext.Provider value={{ isOpen, toggle }}>{children}</CollapsibleContext.Provider>
}

interface CollapsibleTriggerProps {
  children: React.ReactNode
  asChild?: boolean
}

export const CollapsibleTrigger: React.FC<CollapsibleTriggerProps> = ({ children, asChild }) => {
  const { toggle } = useCollapsibleContext()

  if (asChild) {
    return React.cloneElement(children as React.ReactElement, { onClick: toggle })
  }

  return <button onClick={toggle}>{children}</button>
}

interface CollapsibleContentProps {
  children: React.ReactNode
}

export const CollapsibleContent: React.FC<CollapsibleContentProps> = ({ children }) => {
  const { isOpen } = useCollapsibleContext()

  if (!isOpen) return null

  return <div>{children}</div>
}

const CollapsibleContext = React.createContext<{
  isOpen: boolean
  toggle: () => void
} | null>(null)

export const useCollapsibleContext = () => {
  const context = React.useContext(CollapsibleContext)
  if (!context) {
    throw new Error("Collapsible components must be used within a Collapsible")
  }
  return context
}

// Custom component to show the correct arrow based on collapsible state
export const CollapsibleArrow: React.FC = () => {
  const { isOpen } = useCollapsibleContext()
  return isOpen ? (
    <ChevronUp className="text-blue-600 flex-shrink-0" size={24} />
  ) : (
    <ChevronDown className="text-blue-600 flex-shrink-0" size={24} />
  )
}

